import { useState, useEffect, useMemo, useRef, useCallback, FormEvent, ChangeEvent } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  InputAdornment,
  TextField as MuiTextField,
} from '@mui/material';
import { Field, useField, useFormState } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';

import { TextField, IconButton, SearchIcon } from '@occc-app/design-system';
import {
  useKioskAssetQuery,
  KioskAdScheduleInput,
  OfferFilter,
  Publication_Status_Type,
  Offer_Engagement_Type,
  useOfferQuery,
} from '@occc/graphql';
import { useChainId } from '@occc-app/Chain/hooks';
import { OfferSelect } from '@occc-app/routes/CampaignComponents/components/OfferSelect';

import { AssignAssetModal } from './assign-asset-modal.component';
import { AdSetScheduleSection } from './ad-set-schedule-section.component';

import { MAX_ASSET_NAME_LENGTH, MAX_ASSET_DESCRIPTION_LENGTH } from '../../touchpoint.constants';
import { SelectTargetSection } from '../select-target-section.component';
import { KioskSelectionDialog } from '../kiosk-selection-dialog.component';

type AdSetFormProps = {
  handleSubmit: (e?: FormEvent<HTMLFormElement>) => void;
  initialOfferId?: string;
};

export const AdSetForm = ({ handleSubmit, initialOfferId }: AdSetFormProps) => {
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [isKioskDialogOpen, setIsKioskDialogOpen] = useState(false);
  const [kioskIdsInputValue, setKioskIdsInputValue] = useState('');
  const hasInitializedKioskIds = useRef(false);

  const assetIdField = useField<string | null>('assetId', { subscription: { value: true } });
  const kioskIdsField = useField<string[]>('kioskIds', { subscription: { value: true } });
  const offerField = useField<{ id: string; name: string } | null>('offer', {
    subscription: { value: true },
  });
  const { fields: scheduleFields } = useFieldArray<KioskAdScheduleInput>('schedules');
  const { errors, submitFailed } = useFormState({
    subscription: { errors: true, submitFailed: true },
  });

  const kioskIds = useMemo(() => kioskIdsField.input.value || [], [kioskIdsField.input.value]);

  useEffect(() => {
    if (!hasInitializedKioskIds.current && kioskIds.length > 0) {
      hasInitializedKioskIds.current = true;
      setKioskIdsInputValue(kioskIds.join(', '));
    }
  }, [kioskIds]);

  const scheduleErrors = useMemo(() => {
    if (!submitFailed || !errors?.['schedules']) return [];
    return errors['schedules'] as Array<{ endTime?: string; days?: string }>;
  }, [errors, submitFailed]);

  const kioskIdsError = useMemo(() => {
    if (!submitFailed || !errors?.['kioskIds']) return undefined;
    return errors['kioskIds'] as string;
  }, [errors, submitFailed]);

  const chainId = useChainId();
  const hasSetInitialOffer = useRef(false);

  const { data: offerData } = useOfferQuery({
    variables: { filter: { idEquals: initialOfferId } },
    skip: !initialOfferId,
  });

  const { data: assetData } = useKioskAssetQuery({
    variables: { id: assetIdField.input.value || '' },
    skip: !assetIdField.input.value,
  });

  useEffect(() => {
    const offerNode = offerData?.offers.edges[0]?.node;
    if (offerNode && !hasSetInitialOffer.current) {
      hasSetInitialOffer.current = true;
      offerField.input.onChange({ id: offerNode.id, name: offerNode.name });
    }
  }, [offerData, offerField]);

  const makeOffersFilter = useMemo(
    () =>
      (searchValue: string): OfferFilter => ({
        chainIdEquals: chainId,
        publicationStatusEquals: Publication_Status_Type.Published,
        engagementTypeIncludes: [Offer_Engagement_Type.Purchaseable],
        deleted: false,
        ...(searchValue ? { search: searchValue } : {}),
      }),
    [chainId],
  );

  const handleKioskSelection = useCallback(
    (selectedIds: string[]) => {
      kioskIdsField.input.onChange(selectedIds);
      setKioskIdsInputValue(selectedIds.join(', '));
    },
    [kioskIdsField],
  );

  const handleKioskIdsInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setKioskIdsInputValue(event.target.value);
  }, []);

  const openAssetModal = () => setIsAssetModalOpen(true);
  const closeAssetModal = () => setIsAssetModalOpen(false);
  const openKioskDialog = useCallback(() => {
    const ids = kioskIdsInputValue
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    kioskIdsField.input.onChange(ids);
    setIsKioskDialogOpen(true);
  }, [kioskIdsInputValue, kioskIdsField]);
  const closeKioskDialog = () => setIsKioskDialogOpen(false);

  const handleAssetSelect = (assetId: string) => assetIdField.input.onChange(assetId);
  const handleAddSchedule = () => scheduleFields.push({ days: [], startTime: '', endTime: '' });
  const handleRemoveSchedule = (index: number) => scheduleFields.remove(index);
  const handleScheduleChange = (index: number, value: KioskAdScheduleInput) =>
    scheduleFields.update(index, value);

  const assetDisplayName = assetData?.kioskAsset?.name || '';

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Box mb={2}>
          <Card>
            <CardHeader
              title={
                <Typography variant='subtitle1' paragraph>
                  Ad Set Details
                </Typography>
              }
            />
            <CardContent>
              <Stack direction='row' spacing={3}>
                <Field name='name'>
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      label='Ad Set Name'
                      required
                      error={meta.touched && meta.error}
                      inputProps={{ maxLength: MAX_ASSET_NAME_LENGTH }}
                    />
                  )}
                </Field>
                <Field name='description'>
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      label='Ad Set Description'
                      required
                      error={meta.touched && meta.error}
                      inputProps={{ maxLength: MAX_ASSET_DESCRIPTION_LENGTH }}
                    />
                  )}
                </Field>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box mb={2}>
          <Stack direction='row' spacing={2}>
            <Card sx={{ flex: 1 }}>
              <CardHeader
                title={
                  <Typography variant='subtitle1' paragraph>
                    Assign Asset
                  </Typography>
                }
              />
              <CardContent>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  Add an existing asset you've previously uploaded.
                </Typography>
                <MuiTextField
                  name='assetId'
                  label='Asset'
                  value={assetDisplayName}
                  fullWidth
                  required
                  onClick={openAssetModal}
                  InputProps={{
                    readOnly: true,
                    sx: { cursor: 'pointer' },
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton onClick={openAssetModal} size='small' edge='end'>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ sx: { cursor: 'pointer' } }}
                />
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardHeader
                title={
                  <Typography variant='subtitle1' paragraph>
                    Configure the Reward
                  </Typography>
                }
              />
              <CardContent>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  Add an existing Reward to be displayed in the ad set.
                </Typography>
                <OfferSelect name='offer' label='Reward' makeOffersFilter={makeOffersFilter} />
              </CardContent>
            </Card>
          </Stack>
        </Box>

        <Box mb={2}>
          <SelectTargetSection
            title='Target'
            displayValue={kioskIdsInputValue}
            onOpenKioskDialog={openKioskDialog}
            onChange={handleKioskIdsInputChange}
            error={kioskIdsError}
          />
        </Box>

        <AdSetScheduleSection
          schedules={scheduleFields.value || []}
          onAdd={handleAddSchedule}
          onRemove={handleRemoveSchedule}
          onChange={handleScheduleChange}
          errors={scheduleErrors}
        />
      </form>

      <AssignAssetModal
        open={isAssetModalOpen}
        onClose={closeAssetModal}
        onConfirm={handleAssetSelect}
        selectedAssetId={assetIdField.input.value}
      />

      <KioskSelectionDialog
        open={isKioskDialogOpen}
        onClose={closeKioskDialog}
        onConfirm={handleKioskSelection}
        initialKioskIds={kioskIds}
      />
    </>
  );
};
